import { browserStandard } from '../../shared/buehnen-browser-standard';
/**
 * DIE BUEHNE EINER EINZELNEN KARTE, ALS EIGENE SEITE — fuer den Selbst-Screenshot.
 *
 * WOZU DAS DA IST (Rafael, 06.09. 12:26Z + 07.09.)
 * "Er soll immer vorher einen Screenshot machen, und erst wenn er sich selbst
 * gescreenshotet hat ... kann er sagen, dass es ein offenes Thema ist." Eine
 * Session, die eine Guide-Karte anlegt, muss ihr eigenes Fenster (die Buehne, siehe
 * GuidePanel.tsx) ansehen koennen, BEVOR sie "bereit" meldet.
 *
 * WARUM EINE EIGENE SEITE UND NICHT RAFAELS ECHTER GUIDE
 * Bis 06.09. abends schrieb der alte Ritus das Rezept in das LAYOUT eines echten
 * Arbeitsbereichs (`POST /api/layouts`). Ein Arbeitsbereich hat aber nur ein
 * Layout — bei mehreren offenen Karten ueberschrieben sie sich gegenseitig, und
 * Rafaels eigenes Fenster gleich mit (siehe GuidePanel.tsx `herrichten`). Diese
 * Seite schreibt NICHTS: sie ist ein Deep-Link (`/?buehne=<karteId>`), der in einem
 * EIGENEN, frischen Browser (server/routes/screenshots.ts: `openCuiPanel`-Familie,
 * `/api/capture/buehne`) geoeffnet wird. Was dort passiert, betrifft niemanden
 * ausser diesem einen Tab.
 *
 * WIE SIE DAS SCHAFFT, OHNE IN DEN ECHTEN ARBEITSBEREICH ZU SCHREIBEN
 * `layoutKey` bekommt eine SYNTHETISCHE Kennung (`__buehnen-vorschau__<karteId>`),
 * nicht die echte `projectId`. LayoutManager persistiert Layouts ausschliesslich
 * unter `layoutKey ?? projectId` (siehe dort) — mit einer synthetischen Kennung
 * landet ein etwaiges Autosave in einer neuen, folgenlosen Datei, nie in Rafaels
 * echtem `<projectId>.json`. `projectId`/`workDir` bleiben dagegen ECHT: die
 * Panels im Rezept (Chat, Browser, ...) brauchen den echten Kontext, um genau das
 * zu zeigen, was Rafael spaeter sehen wird. Die Buehne selbst (`buehne`-Prop) wird
 * nie gespeichert (LayoutManager-Kommentar: "Bewusst OHNE onModelChange") — sie
 * lebt nur im Speicher dieses Tabs.
 *
 * FEHLER SIND LAUT, NICHT STILL
 * Karte nicht gefunden, kein Rezept, unbekanntes Panel, kein Ziel-Arbeitsbereich,
 * kaputtes Rezept-Dokument — jeder dieser Faelle rendert eine Fehlermeldung mit
 * `data-buehnen-vorschau-fehler`, NIE eine leere oder falsche Buehne. Der
 * Capture-Endpunkt (`/api/capture/buehne`) muss genau dieses Attribut zuerst
 * pruefen, bevor er irgendetwas fotografiert.
 */

import { useEffect, useState, useRef } from 'react';
import { Model, type IJsonModel } from 'flexlayout-react';
import LayoutManager from './LayoutManager';
import ViewControl from './ViewControl';
import { layoutAusRezept, auswahlFuerKarte, BEKANNTE_PANELS, type Layoutrezept } from './GuidePanel';

const API = '/api';

/*
 * `workDir` IST NICHT IMMER DA, UND DER TYP HAT DAS BIS 10.09.2026 VERSCHWIEGEN.
 *
 * Fuer eine Karte des ANDEREN Hauses nennt der Server bewusst keinen Pfad: der
 * Guide-Code laeuft im Dev-Haus, die Arbeitsbereiche des Partner-Hauses liegen dort
 * nicht (`server/routes/guide.ts`, seit 72cb2090d). Er liefert dann ein `workspace`
 * ohne `workDir` — oder gar keins.
 *
 * Der Typ behauptete trotzdem `workDir: string`, und die Bühne reichte den
 * undefinierten Wert an LayoutManager weiter, wo `workDir.split('/')` die ganze
 * Flaeche mit „Component Crash: Cannot read properties of undefined (reading
 * 'split')" abraeumte. Rafael, 10.09. 10:5xZ, auf den Deep-Link einer Partner-Karte:
 * „wieso kann ich die Buehne nicht lesen". Betroffen war JEDE Buehne des anderen
 * Hauses mit einer Browser-Kachel, seit dem 09.09.
 *
 * Der Typ sagt jetzt die Wahrheit. Wer `workDir` braucht, muss mit seinem Fehlen
 * umgehen — sichtbar, nicht durch einen erfundenen Pfad.
 */
interface BuehneWorkspace { id: string; name: string; workDir?: string; quelle: 'gesetzt' | 'vorschlag' }
interface BuehneAntwort {
  titel: string; layout: Layoutrezept; workspace: BuehneWorkspace | null;
  /** Haus der Buehne (nur `buehne:`-Vorschau) — Vorschau-Kacheln lesen ihre Dateien dort. */
  haus?: 'dev' | 'partner';
  /** Nur bei einer Karten-Buehne vorhanden — fuer den Reiter, der bei geteilten Rezepten (Geschwisterkarten) vorn stehen soll. */
  grundlage?: string; wartet_auf?: string[];
}

/**
 * `thema:<themaId>` ist derselbe `buehne`-Deep-Link-Wert wie eine Karten-ID, nur
 * mit Praefix (siehe screenshots.ts, `/api/capture/buehne`-Kommentar) — eine
 * Arbeitsbuehne hat keine Karte, deren Rezept man ansehen koennte; das Rezept
 * haengt am Thema selbst (`GET /guide/themen/:themaId/buehne`).
 */
const THEMA_PRAEFIX = 'thema:';
/**
 * `buehne:<id>` — das BUEHNEN-OBJEKT selbst, seit 11.09.2026.
 *
 * Es ist der Fall, den es geben muesste, seit es Buehnen gibt: `buehnen-bilder.py`
 * fotografiert ueber die erste KARTE einer Buehne, und eine Buehne ohne Entscheidung
 * — die haeufigste Form, jede neue faengt so an — hatte damit kein Bild. Seit die
 * Entscheidungen der Buehne selbst gehoeren, gibt es diese Karte gar nicht mehr; ohne
 * diesen Weg waere jeder Abgang eine benannte Luecke, und die Zusage "kein Abgang
 * ohne Bild" waere nur noch Text.
 *
 * Dieselbe Praefix-Form wie `thema:` und aus demselben Grund: App.tsx reicht
 * `?buehne=` unveraendert durch und muss nicht angefasst werden.
 */
const BUEHNE_PRAEFIX = 'buehne:';

function buehnenEndpunkt(karteId: string): string {
  if (karteId.startsWith(BUEHNE_PRAEFIX)) {
    return `${API}/guide/buehnen/${encodeURIComponent(karteId.slice(BUEHNE_PRAEFIX.length))}/vorschau`;
  }
  if (karteId.startsWith(THEMA_PRAEFIX)) {
    return `${API}/guide/themen/${encodeURIComponent(karteId.slice(THEMA_PRAEFIX.length))}/buehne`;
  }
  return `${API}/guide/karten/${encodeURIComponent(karteId)}/buehne`;
}

type Zustand =
  | { art: 'laedt' }
  | { art: 'fehler'; text: string; titel?: string }
  | { art: 'bereit'; doc: unknown; workspace: BuehneWorkspace | null };

function pruefeUnbekanntePanels(rezept: Layoutrezept): string[] {
  return rezept.kacheln
    .filter(k => k.art === 'panel' && !BEKANNTE_PANELS.has(k.component ?? ''))
    .map(k => k.component ?? '(ohne Namen)');
}

const box: React.CSSProperties = {
  height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--tn-bg)', color: 'var(--tn-text-muted)', fontSize: 13, textAlign: 'center', padding: 24,
};

/** Hinweis fuer einen Partner, dessen Deep-Link auf eine fremde Buehne zeigt (Server: 403). */
export const NICHT_DEIN_KONTO = 'Diese Bühne gehört nicht zu deinem Konto.';

/** `partnerSicht`: ein angemeldeter Nicht-Admin oeffnet `?buehne=` — 401/403 bekommen einen klaren Satz statt einer Fehlermeldung. */
export default function BuehnenVorschau({ karteId, partnerSicht = false }: { karteId: string; partnerSicht?: boolean }) {
  const params = new URLSearchParams(window.location.search);
  const [rahmen, setRahmen] = useState(params.get('rahmen') === 'standard');
  const [chat, setChat] = useState(params.get('chat') === 'offen');
  const [mass, setMass] = useState('');
  const stageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setMass(`${Math.round(entry.contentRect.width)} × ${Math.round(entry.contentRect.height)} px`));
    observer.observe(el);
    return () => observer.disconnect();
  });
  const [zustand, setZustand] = useState<Zustand>({ art: 'laedt' });

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      const r = await fetch(buehnenEndpunkt(karteId));
      const body = await r.json().catch(() => ({}));
      if (abgebrochen) return;
      if (!r.ok) {
        if (partnerSicht && r.status === 403) {
          setZustand({ art: 'fehler', titel: NICHT_DEIN_KONTO, text: body.error || 'Als Arbeitsplatz öffnest du nur deine eigenen Bühnen.' });
          return;
        }
        if (partnerSicht && r.status === 401) {
          setZustand({ art: 'fehler', titel: 'Nicht angemeldet', text: 'Bitte melde dich an und öffne den Link erneut.' });
          return;
        }
        setZustand({ art: 'fehler', text: body.error || `Buehne liess sich nicht laden (${r.status}).` });
        return;
      }
      const antwort = body as BuehneAntwort;
      // Rezeptentwurf ist ein Werkzeug der Buehnen-Bauer (Admin); ein Partner sieht immer das echte Rezept.
      const entwurf = partnerSicht ? null : new URLSearchParams(window.location.search).get('rezept');
      if (entwurf) {
        const url = new URL(entwurf, window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.startsWith('/downloads/entscheidungen/') || !url.pathname.endsWith('.json')) {
          throw new Error('Der Rezeptentwurf muss eine JSON-Datei unter /downloads/entscheidungen/ sein.');
        }
        const response = await fetch(url.href);
        if (!response.ok) throw new Error(`Rezeptentwurf nicht erreichbar (${response.status}).`);
        const layout = await response.json();
        if (!layout || !Array.isArray(layout.kacheln) || !layout.kacheln.length) throw new Error('Rezeptentwurf enthält keine Kacheln.');
        antwort.layout = layout;
        if (abgebrochen) return;
      }
      const unbekannt = pruefeUnbekanntePanels(antwort.layout);
      if (unbekannt.length) {
        setZustand({ art: 'fehler', text: `Rezept nennt unbekannte Panels: ${unbekannt.join(', ')}` });
        return;
      }
      let doc: unknown;
      try {
        const gebaut = layoutAusRezept(antwort.layout, null, auswahlFuerKarte(antwort), antwort.haus === 'dev' || antwort.haus === 'partner' ? antwort.haus : undefined);
        doc = browserStandard(gebaut.doc as IJsonModel, { haus: antwort.haus, origin: window.location.origin });
        // Vorab-Pruefung mit demselben Aufbau, den LayoutManager gleich intern
        // macht (`Model.fromJson`) — schlaegt sie hier fehl, wuerde LayoutManager
        // still auf den (leeren) Arbeitsbereich darunter zurueckfallen und ein
        // FALSCHES, volles Bild waere die Folge. Siehe Datei-Kommentar oben.
        Model.fromJson(doc as IJsonModel);
      } catch (e: any) {
        setZustand({ art: 'fehler', text: `Rezept liess sich nicht als Buehne bauen: ${e?.message ?? e}` });
        return;
      }
      setZustand({ art: 'bereit', doc, workspace: antwort.workspace });
    })().catch((e: any) => {
      if (!abgebrochen) setZustand({ art: 'fehler', text: `Buehne liess sich nicht aufbauen: ${e?.message ?? e}` });
    });
    return () => { abgebrochen = true; };
  }, [karteId, partnerSicht]);

  if (zustand.art === 'laedt') {
    return <div style={box}>Lade Buehne „{karteId}" …</div>;
  }
  if (zustand.art === 'fehler') {
    return (
      <div style={box}>
        <div data-buehnen-vorschau-fehler={zustand.text}>
          <div style={{ fontSize: 16, marginBottom: 8, color: 'var(--tn-text)' }}>{zustand.titel ?? `Keine Buehne fuer „${karteId}"`}</div>
          <div>{zustand.text}</div>
          {partnerSicht && <div style={{ marginTop: 12 }}><a href="/">Zum CUI</a></div>}
        </div>
      </div>
    );
  }

  return (
    <div
      data-buehnen-vorschau={karteId}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--tn-bg)', overflow: 'hidden' }}
    >
      {!zustand.workspace?.workDir && (
        /* Gesagt, nicht verschwiegen: ohne Arbeitsbereich bleiben die Kacheln auf das
           angewiesen, was im Rezept steht. Eine Browser-Kachel mit eigener Adresse laedt
           normal; eine ohne bliebe leer, und dann soll hier stehen, warum. */
        <div style={{ flex: '0 0 auto', padding: '3px 10px', fontSize: 11, color: 'var(--tn-orange)',
                      borderBottom: '1px solid var(--tn-border)' }}>
          Ohne Arbeitsbereich gebaut — die Karte gehoert dem anderen Haus. Kacheln mit eigener
          Adresse laden normal; eine Kachel ohne Adresse bleibt leer.
        </div>
      )}
      <div className="cui-stage-preview-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '3px 8px', minHeight: 28, boxSizing: 'border-box', fontSize: 11, flexShrink: 0 }}>
        <label><input type="checkbox" checked={rahmen} onChange={e => setRahmen(e.target.checked)} /> Im Standardrahmen prüfen</label>
        {rahmen && <label><input type="checkbox" checked={chat} onChange={e => setChat(e.target.checked)} /> Platz für Berater-Chat</label>}
        <span data-stage-measure>{mass}</span>
        <ViewControl />
        {params.get('rezept') && <span>Rezeptentwurf · nur Vorschau</span>}
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {rahmen && <aside aria-label="Platzbedarf Guide und Kanzler" style={{ flex: '0 0 26%', display: 'flex', flexDirection: 'column', borderRight: '6px solid var(--tn-border)', color: 'var(--tn-text-muted)' }}>
          <div style={{ flex: '0 0 60%', padding: 16 }}>Guide · Entscheidungen<br /><small>Platzhalter für die Größenprüfung</small></div>
          <div style={{ display: 'flex', flex: 1, borderTop: '6px solid var(--tn-border)' }}><div style={{ flex: 1, padding: 12 }}>Kanzler</div><div style={{ flex: 1, padding: 12, borderLeft: '1px solid var(--tn-border)' }}>Vizekanzler</div></div>
        </aside>}
        <div ref={stageRef} data-stage-workspace style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}>
      <LayoutManager
        projectId={zustand.workspace?.id ?? karteId}
        layoutKey={`__buehnen-vorschau__${karteId}`}
        workDir={zustand.workspace?.workDir}
        isActive
        buehne={zustand.doc}
      />
        </div>
        {rahmen && <aside aria-label="Platzbedarf Berater" style={{ flex: '0 0 auto', width: chat ? 'min(420px, 40vw)' : 32, borderLeft: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)', overflow: 'hidden' }}><span style={{ writingMode: 'vertical-rl', padding: 6 }}>Berater{chat ? ' · Platzhalter' : ''}</span></aside>}
      </div>
    </div>
  );
}
